import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { navigate } from 'gatsby'
import analytics from 'global/AnalyticsData'
import { userContext } from 'global/context/sub-contexts/userContext'
import { BreakConversionModal } from 'components/modal/submodals/BreakConversionModal'
import { formatStepImageUrl } from 'util/beaker2-step-image-url'
import {
  generateImage,
  getImageEnhancementStatus,
  serializeInputValues,
} from 'services/image-enhancement'

import { defaultSelection, isChecklist, toggleSelection } from './utils/checklist'

import RichText from './components/RichText'
import UploadStage from './components/UploadStage'
import CustomizeStage from './components/CustomizeStage'
import PreviewStage from './components/PreviewStage'
import * as styles from './ImageEnhancement.module.scss'

const STAGE = {
  UPLOAD: 'upload',
  CUSTOMIZE: 'customize',
  GENERATING: 'generating',
  PREVIEW: 'preview',
  LIMIT: 'limit',
  DAILY_LIMIT: 'dailyLimit',
  ERROR: 'error',
}

// Fallback rotating copy when the campaign hasn't set its own generating messages.
const DEFAULT_GENERATING_MESSAGES = [
  'Warming up the studio...',
  'Analyzing your photo...',
  'Setting the scene...',
  'Adding the finishing touches...',
  'Almost ready...',
]

const GENERATING_MESSAGE_INTERVAL_MS = 3500
const STATUS_CHECK_ATTEMPTS = 3
const STATUS_CHECK_RETRY_MS = 1500

// Roughly how long a generation takes - the "building blocks" grid fills over this span.
const BUILD_DURATION_MS = 60000
const BUILD_COLS = 8

// Builds a grid of cells (row-major, top-left to bottom-right) sized to the output ratio.
// Each cell gets a staggered reveal delay + slight opacity variation so it reads as an
// image progressively rendering rather than a flat color block.
function buildCells(aspectRatio) {
  const parts = String(aspectRatio).split('/')
  const w = parseFloat(parts[0]) || 1
  const h = parseFloat(parts[1]) || 1
  const rows = Math.max(4, Math.round(BUILD_COLS * (h / w)))
  const total = BUILD_COLS * rows
  return {
    cols: BUILD_COLS,
    rows,
    cells: Array.from({ length: total }, (_, i) => ({
      delay: (i / total) * BUILD_DURATION_MS,
      opacity: 0.2 + ((i * 9301 + 49297) % 233280) / 233280 * 0.3,
    })),
  }
}

/*
  Maps the CMS imageEnhancement config into the shape this step renders.
  Reference images / base images / the base prompt are intentionally NOT read here - they stay
  server-side (the API resolves them from stepConfigId). The client only needs display + inputs.
*/
function stepToConfig(step) {
  const ie = step.imageEnhancement || {}

  const inputs = (ie.inputs || [])
    .filter(i => i && i.key)
    .map(i => ({
      key: i.key,
      label: i.label || i.key,
      helpText: i.helpText || '',
      type: i.type || 'SELECT',
      required: !!i.required,
      placeholder: i.placeholder || '',
      maxLength: i.maxLength || 40,
      // CHECKLIST only: how many options the user must tick and how many they may tick.
      // 0 means "no minimum beyond what `required` implies" and, for the cap, no cap at
      // all - the author types 0 to make the whole list selectable.
      minSelections: Number(i.minSelections) || 0,
      maxSelections: Number(i.maxSelections) || 0,
      options: (i.options || []).map(o => ({
        value: o.value,
        title: o.title || '',
        description: o.description || '',
        thumbnailId: o.thumbnailId || undefined,
        default: !!o.default,
      })),
    }))

  // Output aspect ratio - drives the upload dropzone, generating skeleton, and result frame
  // so the placeholder matches the final image shape.
  const sizeToRatio = {
    '1024x1024': '1 / 1',
    '1024x1536': '2 / 3',
    '1536x1024': '3 / 2',
    auto: '2 / 3',
  }

  return {
    // The step-builder record id - the locked config source the API reads.
    stepConfigId: step.originalStepId || step.stepId,
    aspectRatio: sizeToRatio[ie.imageSize] || '2 / 3',
    theme: {
      bgPrimary: ie.bgColor || '#051a61',
      bgSecondary: ie.bgColor || '#0a2370',
      brandPrimary: ie.brandColor || '#2659e5',
      // Optional: card surface, defaults to the step background.
      cardBg: ie.cardColor || undefined,
      // Optional: when unset, buttons darken their brand color on hover instead.
      brandHover: ie.brandHoverColor || undefined,
      textOnDark: ie.textColor || '#ffffff',
      accent: ie.accentColor || '#e0edff',
    },
    headerImageId: ie.headerImageId || undefined,
    heroImageId: ie.heroImageId || undefined,
    // Border is on unless the CMS turns it off.
    cardBorder: ie.cardBorder !== false,
    footerText: ie.footerText || '',
    copy: {
      uploadTitle: ie.uploadTitle || 'Upload Your Photo',
      uploadSubtitle: ie.uploadSubtitle || '',
      uploadCtaText: ie.uploadCtaText || '',
      uploadHelpText: ie.uploadHelpText || '',
      customizeTitle: ie.customizeTitle || 'Make It Yours',
      customizeSubtitle: ie.customizeSubtitle || '',
      generateCtaText: ie.generateCtaText || '',
      generatingTitle: ie.generatingTitle || 'Creating your image...',
      generatingSubtitle: ie.generatingSubtitle || 'This usually takes a few seconds.',
      generatingMessages: (ie.generatingMessages || []).filter(m => m && m.trim()),
      previewTitle: ie.previewTitle || 'Here You Are!',
      previewSubtitle: ie.previewSubtitle || '',
      previewDisclaimer: ie.previewDisclaimer || 'AI generated',
      downloadCtaText: ie.downloadCtaText || '',
      regenerateCtaText: ie.regenerateCtaText || '',
      shareCtaText: ie.shareCtaText || '',
      shareTitle: ie.shareTitle || '',
      shareText: ie.shareText || '',
      shareUrl: ie.shareUrl || '',
      outOfTriesText: ie.outOfTriesText || "That's all your tries for now",
      limitReachedTitle: ie.limitReachedTitle || "You're all out",
      limitReachedSubtitle:
        ie.limitReachedSubtitle || "You've used all your generations for this campaign.",
      dailyLimitTitle: ie.dailyLimitTitle || "Today's limit reached",
      dailyLimitSubtitle:
        ie.dailyLimitSubtitle ||
        'This campaign hit its daily generation limit. Come back tomorrow!',
      errorTitle: ie.errorTitle || 'Something went wrong',
      errorSubtitle: ie.errorSubtitle || 'Please try again in a moment.',
      moderationBlockedMessage:
        ie.moderationBlockedMessage ||
        "We couldn't process that - please try a different photo or option.",
    },
    inputs,
    checkboxes: (ie.checkboxes || []).map(c => ({
      id: c.id,
      label: c.label || '',
      required: !!c.required,
    })),
  }
}

const ImageEnhancementStep = ({
  step,
  breakId,
  onEngage,
  onAwardPoints,
  onChangeBackground,
}) => {
  const config = useMemo(() => stepToConfig(step), [step])
  const buildGrid = useMemo(() => buildCells(config.aspectRatio), [config.aspectRatio])

  const [stage, setStage] = useState(STAGE.UPLOAD)
  const [file, setFile] = useState(null)
  const [originalUrl, setOriginalUrl] = useState(null)
  const [values, setValues] = useState(() => {
    // Preselect any option flagged default. A CHECKLIST takes every default (in author
    // order, and never more than its cap); every other input type takes the first one, as
    // it always has.
    const initial = {}
    config.inputs.forEach(i => {
      if (isChecklist(i)) {
        const defaults = defaultSelection(i)
        if (defaults.length) initial[i.key] = defaults
        return
      }
      const def = (i.options || []).find(o => o.default)
      if (def) initial[i.key] = def.value
    })
    return initial
  })
  const [checked, setChecked] = useState({})
  const [resultUrl, setResultUrl] = useState(null)
  const [remaining, setRemaining] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [msgIndex, setMsgIndex] = useState(0)
  const [breakConversionOpen, setBreakConversionOpen] = useState(false)

  const user_context = useContext(userContext)

  const generatingMessages = config.copy.generatingMessages.length
    ? config.copy.generatingMessages
    : DEFAULT_GENERATING_MESSAGES

  // Rotate the generating messages while the image is being created.
  useEffect(() => {
    if (stage !== STAGE.GENERATING) return undefined
    setMsgIndex(0)
    const id = setInterval(() => {
      setMsgIndex(i => (i + 1) % generatingMessages.length)
    }, GENERATING_MESSAGE_INTERVAL_MS)
    return () => clearInterval(id)
  }, [stage, generatingMessages.length])

  // Paint the step panel with the configured background.
  useEffect(() => {
    onChangeBackground && onChangeBackground({ backgroundColor: config.theme.bgPrimary })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.theme.bgPrimary])

  // Check the limits whenever the user is about to spend effort (entering upload or
  // customize), not just on mount: limits can be exhausted mid-session by this user's own
  // generations or by other users hitting the campaign's daily cap. The generate call still
  // enforces everything server-side.
  useEffect(() => {
    if (stage !== STAGE.UPLOAD && stage !== STAGE.CUSTOMIZE) return undefined
    let cancelled = false
    ;(async () => {
      // Retry a few times: the first check races the guest registration that happens on
      // step open, and a dropped request here would otherwise skip the pre-upload limit
      // screen entirely (the next check only happens on the next stage change).
      for (let attempt = 0; attempt < STATUS_CHECK_ATTEMPTS && !cancelled; attempt++) {
        try {
          const res = await getImageEnhancementStatus({
            stepId: step.id,
            stepConfigId: config.stepConfigId,
          })
          if (cancelled) return
          if (!res || !res.ok) throw new Error('status check failed')
          const data = await res.json()
          if (cancelled || typeof data.remainingGenerations !== 'number') return
          setRemaining(data.remainingGenerations)
          if (data.dailyLimitReached) {
            setStage(STAGE.DAILY_LIMIT)
          } else if (data.remainingGenerations <= 0) {
            setStage(STAGE.LIMIT)
          }
          return
        } catch (e) {
          await new Promise(resolve => setTimeout(resolve, STATUS_CHECK_RETRY_MS))
        }
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage])

  const track = useCallback(
    (eventName, data = {}) => {
      analytics.stepEvent(eventName, step, data)
    },
    [step],
  )

  const handleUploadContinue = uploadedFile => {
    setFile(uploadedFile)
    setOriginalUrl(URL.createObjectURL(uploadedFile))
    track('step_interaction_image_enhancement_photo_selected')
    setStage(STAGE.CUSTOMIZE)
  }

  /*
    `multiple` comes from a CHECKLIST input: the value is toggled in or out of an array
    instead of replacing what is there. The array is kept in the author's option order so
    the same set of ticks always produces the same prompt.
  */
  const handleChangeValue = (key, value, { multiple = false } = {}) => {
    if (!multiple) {
      setValues(prev => ({ ...prev, [key]: value }))
      return
    }
    const input = config.inputs.find(i => i.key === key)
    // toggleSelection filters against the option list, so without the input every value
    // would be dropped - leave the selection alone instead.
    if (!input) return
    setValues(prev => ({
      ...prev,
      [key]: toggleSelection(prev[key], value, input.options),
    }))
  }

  const handleToggleCheckbox = id => {
    setChecked(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const handleGenerate = async () => {
    setStage(STAGE.GENERATING)
    // Send the same comma-joined strings the API gets. The GTM push nests this under
    // `break` and the warehouse event flattens it into one map, and an array survives
    // neither shape cleanly - a string keeps both destinations reading the same value.
    track('step_interaction_image_enhancement_generate', {
      inputs: serializeInputValues(values),
    })
    try {
      const response = await generateImage({
        breakId,
        stepId: step.id,
        stepConfigId: config.stepConfigId,
        inputs: values,
        file,
      })

      if (response.status === 401) {
        // Session expired / logged out: prompt the platform's signup-or-login modal
        // (same flow EditorialStep3 and BreakGate use) and return to the customize
        // stage so the user can retry after signing in.
        setBreakConversionOpen(true)
        setStage(STAGE.CUSTOMIZE)
        return
      }
      if (response.status === 429) {
        // Distinguish the campaign-wide daily cap from the per-user limit so the
        // user is not told "you are out" when it is the campaign that is out.
        let isDaily = false
        try {
          const body = await response.json()
          isDaily = /daily/i.test(body?.message || '')
        } catch (e) {
          // fall through to the per-user limit screen
        }
        setStage(isDaily ? STAGE.DAILY_LIMIT : STAGE.LIMIT)
        return
      }
      if (!response.ok) {
        setErrorMessage(config.copy.errorSubtitle)
        setStage(STAGE.ERROR)
        return
      }

      const data = await response.json()
      if (data.moderationFlagged) {
        setErrorMessage(config.copy.moderationBlockedMessage)
        setStage(STAGE.ERROR)
        return
      }

      setResultUrl(data.resultImageUrl)
      setRemaining(data.remainingGenerations)
      setStage(STAGE.PREVIEW)
      track('step_interaction_image_enhancement_generated')
    } catch (e) {
      setErrorMessage(config.copy.errorSubtitle)
      setStage(STAGE.ERROR)
    }
  }

  const handleDownload = async () => {
    try {
      const res = await fetch(resultUrl)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'my-image.png'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e) {
      // Fallback: open in a new tab if the blob download fails.
      window.open(resultUrl, '_blank')
    }
    track('step_interaction_image_enhancement_downloaded')
    onEngage && onEngage()
    onAwardPoints && onAwardPoints(step.possibleCoinsAwarded || 0)
  }

  const handleRegenerate = () => {
    setStage(STAGE.CUSTOMIZE)
  }

  // Preserves the conversion -> sign-up chain old Modal.jsx wired itself
  // (Modal.jsx:34-43): close this dialog, then open the sign-up dialog.
  const handleConversionSignUpClick = () => {
    setBreakConversionOpen(false)
    user_context.askToLogin()
  }

  const handleConversionHowItWorksClick = () => {
    setBreakConversionOpen(false)
    navigate('/how-it-works')
  }

  const handleShare = async () => {
    const shareData = {}
    if (config.copy.shareTitle) shareData.title = config.copy.shareTitle
    if (config.copy.shareText) shareData.text = config.copy.shareText
    if (config.copy.shareUrl) shareData.url = config.copy.shareUrl

    try {
      if (navigator.share) {
        // Try to share the actual image file when the platform supports it.
        try {
          const res = await fetch(resultUrl)
          const blob = await res.blob()
          const file = new File([blob], 'my-image.png', { type: blob.type || 'image/png' })
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ ...shareData, files: [file] })
            track('step_interaction_image_enhancement_shared')
            return
          }
        } catch (e) {
          // fall through to text/url share
        }
        await navigator.share(shareData)
        track('step_interaction_image_enhancement_shared')
      } else if (config.copy.shareUrl) {
        window.open(config.copy.shareUrl, '_blank')
      }
    } catch (e) {
      // user cancelled the share sheet - ignore
    }
  }

  const themeStyle = {
    '--bg-primary': config.theme.bgPrimary,
    '--bg-secondary': config.theme.bgSecondary,
    '--brand-primary': config.theme.brandPrimary,
    '--brand-hover': config.theme.brandHover,
    // With an explicit hover color there is nothing to darken.
    '--brand-hover-shade': config.theme.brandHover ? 'transparent' : undefined,
    '--text-on-dark': config.theme.textOnDark,
    '--accent': config.theme.accent,
    '--card-bg': config.theme.cardBg,
    '--result-ratio': config.aspectRatio,
  }

  // The border is opt-out in the CMS; every screen shares the same card shell.
  const cardClass = `${styles.card} ${
    config.cardBorder === false ? styles.cardBorderless : ''
  }`

  return (
    <div className={styles.root} style={themeStyle}>
      {config.headerImageId && (
        <img
          className={styles.headerImage}
          src={formatStepImageUrl(config.headerImageId)}
          alt=""
        />
      )}

      {stage === STAGE.UPLOAD && (
        <UploadStage
          copy={config.copy}
          cardClassName={cardClass}
          heroImageUrl={config.heroImageId ? formatStepImageUrl(config.heroImageId) : null}
          initialFile={file}
          onContinue={handleUploadContinue}
        />
      )}

      {stage === STAGE.CUSTOMIZE && (
        <CustomizeStage
          copy={config.copy}
          cardClassName={cardClass}
          inputs={config.inputs}
          checkboxes={config.checkboxes}
          values={values}
          checked={checked}
          onChangeValue={handleChangeValue}
          onToggleCheckbox={handleToggleCheckbox}
          onGenerate={handleGenerate}
        />
      )}

      {stage === STAGE.GENERATING && (
        <div className={cardClass}>
          <div className={styles.imageBox}>
            <div
              className={styles.skeleton}
              style={{
                '--build-cols': buildGrid.cols,
                '--build-rows': buildGrid.rows,
              }}
            >
              <div className={styles.buildGrid}>
                {buildGrid.cells.map((cell, i) => (
                  <span
                    key={i}
                    className={styles.buildCell}
                    style={{
                      animationDelay: `${cell.delay}ms`,
                      '--cell-opacity': cell.opacity,
                    }}
                  />
                ))}
              </div>
              <div className={styles.scanLine} />
            </div>
          </div>
          <div className={styles.generatingMeta}>
            <span className={styles.spinner} />
            <RichText as="h2" className={styles.title} html={config.copy.generatingTitle} />
            <p key={msgIndex} className={`${styles.subtitle} ${styles.rotatingMessage}`}>
              {generatingMessages[msgIndex]}
            </p>
          </div>
        </div>
      )}

      {stage === STAGE.PREVIEW && (
        <PreviewStage
          copy={config.copy}
          cardClassName={cardClass}
          resultUrl={resultUrl}
          originalUrl={originalUrl}
          remaining={remaining == null ? 0 : remaining}
          canShare={Boolean(config.copy.shareTitle || config.copy.shareText || config.copy.shareUrl)}
          onDownload={handleDownload}
          onRegenerate={handleRegenerate}
          onShare={handleShare}
        />
      )}

      {stage === STAGE.LIMIT && (
        <div className={cardClass}>
          <div className={styles.cardHeader}>
            <RichText as="h2" className={styles.title} html={config.copy.limitReachedTitle} />
            <RichText as="p" className={styles.subtitle} html={config.copy.limitReachedSubtitle} />
          </div>
        </div>
      )}

      {stage === STAGE.DAILY_LIMIT && (
        <div className={cardClass}>
          <div className={styles.cardHeader}>
            <RichText as="h2" className={styles.title} html={config.copy.dailyLimitTitle} />
            <RichText as="p" className={styles.subtitle} html={config.copy.dailyLimitSubtitle} />
          </div>
        </div>
      )}

      {stage === STAGE.ERROR && (
        <div className={cardClass}>
          <div className={styles.cardHeader}>
            <RichText as="h2" className={styles.title} html={config.copy.errorTitle} />
            <RichText
              as="p"
              className={styles.subtitle}
              html={errorMessage || config.copy.errorSubtitle}
            />
          </div>
          <button
            type="button"
            className={styles.btn}
            onClick={() => setStage(STAGE.CUSTOMIZE)}
          >
            {config.copy.regenerateCtaText}
          </button>
        </div>
      )}

      {config.footerText && (
        <div
          className={styles.footer}
          dangerouslySetInnerHTML={{ __html: config.footerText }}
        />
      )}

      <BreakConversionModal
        open={breakConversionOpen}
        handleClose={() => setBreakConversionOpen(false)}
        onSignUpClick={handleConversionSignUpClick}
        onHowItWorksClick={handleConversionHowItWorksClick}
      />
    </div>
  )
}

export default ImageEnhancementStep
